import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Wrench, Rocket, HeadphonesIcon, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const HowItWorks = () => {
  const steps = [
    {
      number: "01",
      icon: Phone,
      title: "Free Discovery Call",
      description: "We learn how your shop works",
      details: [
        "Tell us about your business and current challenges",
        "We'll explain how our systems can help",
        "No pressure, no tech talk - just a conversation",
        "Ask any questions you have",
        "Get honest advice even if we're not the right fit"
      ],
      image: "https://images.pexels.com/photos/8171203/pexels-photo-8171203.jpeg",
      duration: "30 minutes"
    },
    {
      number: "02",
      icon: Wrench,
      title: "Custom AI Setup",
      description: "Built specifically for your business",
      details: [
        "We build your website with your branding",
        "Set up AI to answer your specific questions",
        "Configure booking for your services and schedule",
        "Connect everything to your phone and email",
        "You approve everything before it goes live"
      ],
      image: "https://images.pexels.com/photos/7414275/pexels-photo-7414275.jpeg",
      duration: "1-2 weeks"
    },
    {
      number: "03",
      icon: Rocket,
      title: "Launch & Test",
      description: "You test it like a real customer",
      details: [
        "We give you a test link to try everything",
        "Call the AI assistant yourself",
        "Book a test appointment",
        "Make sure it all works the way you want",
        "We make any changes you need"
      ],
      image: "https://images.pexels.com/photos/3184430/pexels-photo-3184430.jpeg",
      duration: "1 week"
    },
    {
      number: "04",
      icon: HeadphonesIcon,
      title: "Ongoing Support",
      description: "We maintain and improve it",
      details: [
        "We monitor everything to make sure it's working",
        "Update and improve features regularly",
        "Answer questions and make changes as needed",
        "You focus on running your shop, we handle the tech",
        "No tech headaches for you"
      ],
      image: "https://images.pexels.com/photos/1181605/pexels-photo-1181605.jpeg",
      duration: "Ongoing"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              How It <span className="text-orange-500">Works</span>
            </h1>
            <p className="text-xl text-gray-400">
              A simple, step-by-step process designed to get you up and running fast
            </p>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-24">
            {steps.map((step, idx) => (
              <div key={idx} className="relative">
                {/* Connecting Line */}
                {idx < steps.length - 1 && (
                  <div className="hidden lg:block absolute left-1/2 top-full h-24 w-0.5 bg-gradient-to-b from-orange-500 to-transparent transform -translate-x-1/2 z-0"></div>
                )}

                <div className={`grid lg:grid-cols-2 gap-12 items-center ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  <div className={idx % 2 === 1 ? 'lg:order-2' : ''}>
                    {/* Step Number Badge */}
                    <div className="inline-block mb-6">
                      <div className="flex items-center gap-4">
                        <div className="text-6xl font-bold text-orange-500/20">
                          {step.number}
                        </div>
                        <div className="bg-orange-500/10 w-16 h-16 rounded-xl flex items-center justify-center">
                          <step.icon className="h-8 w-8 text-orange-500" />
                        </div>
                      </div>
                    </div>

                    <h2 className="text-4xl font-bold text-white mb-4">
                      {step.title}
                    </h2>

                    <p className="text-xl text-gray-400 mb-6">
                      {step.description}
                    </p>

                    <div className="inline-block px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full mb-8">
                      <span className="text-orange-400 font-semibold text-sm">Timeline: {step.duration}</span>
                    </div>

                    {/* Details List */}
                    <div className="space-y-3 mb-8">
                      {step.details.map((detail, dIdx) => (
                        <div key={dIdx} className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-1" />
                          <span className="text-gray-300">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={idx % 2 === 1 ? 'lg:order-1' : ''}>
                    <div className="relative">
                      <img 
                        src={step.image}
                        alt={step.title}
                        className="rounded-2xl shadow-2xl shadow-orange-500/20"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-transparent rounded-2xl"></div>
                      
                      {/* Floating Badge */}
                      <div className="absolute top-6 right-6 bg-orange-500 text-white px-6 py-3 rounded-xl font-bold text-lg shadow-lg">
                        Step {idx + 1}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              What You Get at the End
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              A complete system that works for you 24/7
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Professional Website", desc: "Fast, modern, mobile-friendly site that builds trust" },
              { title: "AI Phone Assistant", desc: "Answers calls after hours and captures leads" },
              { title: "Online Booking", desc: "Customers can schedule without calling you" },
              { title: "Automatic Follow-Ups", desc: "Stay in touch with customers on autopilot" },
              { title: "Lead Notifications", desc: "Get instant alerts when someone reaches out" },
              { title: "Monthly Reports", desc: "See how many leads you're capturing" }
            ].map((feature, idx) => (
              <Card key={idx} className="bg-gray-800/50 border-gray-700 hover:border-orange-500/50 transition-all duration-300 hover:translate-y-[-4px]">
                <CardContent className="p-6">
                  <div className="bg-orange-500/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <CheckCircle className="h-6 w-6 text-orange-500" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-6">
              Common Questions
            </h2>
          </div>

          <div className="space-y-6">
            {[
              { q: "How long does setup take?", a: "Typically 2-4 weeks from start to finish. We work fast but don't rush quality." },
              { q: "Do I need to know anything technical?", a: "Nope. We handle everything. You just tell us what you want and we make it happen." },
              { q: "What if I want to make changes later?", a: "Just let us know. We can update anything anytime. That's part of our ongoing support." },
              { q: "Can I cancel anytime?", a: "Yes. No long-term contracts. If it's not working for you, you can cancel with 30 days notice." },
              { q: "What if customers don't like talking to AI?", a: "The AI is trained to sound natural and helpful. Most customers can't tell the difference. And if they prefer a human, the AI captures their info and we notify you immediately." }
            ].map((faq, idx) => (
              <Card key={idx} className="bg-gray-900/50 border-gray-800 hover:border-orange-500/50 transition-all duration-300">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3">{faq.q}</h3>
                  <p className="text-gray-400">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-orange-100 mb-10">
            Book your free discovery call today. No pressure. No tech talk. Just practical ideas.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-orange-600 hover:bg-gray-100 px-10 py-6 text-xl font-bold shadow-2xl transition-all duration-300 hover:scale-105">
              Start With a Free Call
              <ArrowRight className="ml-2 h-6 w-6" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
